(() => {
    if (customElements.get('asteri-location-selector')) return;

    const STORAGE_KEY = 'asteri-location-selected';

    /* ---- Locale facts injected server-side by Liquid ---- */
    const CFG = window.__asteriLocCfg || {};
    const AVAILABLE_COUNTRIES = (CFG.countries || []).map((c) => String(c).toUpperCase());
    const AVAILABLE_LANGUAGES = CFG.languages || [];
    const CURRENT_COUNTRY = (CFG.country || '').toUpperCase();
    const CURRENT_LANGUAGE = (CFG.language || '');

    /* Countries whose shoppers should default to Arabic (when the store has an
       `ar` language). Edit freely. Anything not listed defaults to English. */
    const ARABIC_COUNTRIES = [
      'SA', 'AE', 'KW', 'QA', 'BH', 'OM', 'EG', 'LB', 'JO', 'IQ', 'MA',
      'TN', 'DZ', 'LY', 'YE', 'PS', 'SY', 'SD', 'MR', 'KM', 'DJ', 'SO'
    ];

    /* GeoIP endpoint timeout — after this we give up and open the drawer. */
    const GEO_TIMEOUT_MS = 3500;

    /* Pick an available language whose ISO code starts with `prefix`
       ('ar' or 'en'); fall back sensibly. */
    function pickLanguage(prefix) {
      prefix = prefix.toLowerCase();
      for (const l of AVAILABLE_LANGUAGES) {
        if (String(l).toLowerCase().indexOf(prefix) === 0) return l;
      }
      if (prefix === 'en') {
        for (const l of AVAILABLE_LANGUAGES) {
          if (String(l).toLowerCase().indexOf('ar') !== 0) return l;
        }
      }
      return AVAILABLE_LANGUAGES[0] || CURRENT_LANGUAGE || '';
    }

    /* Country to use when GeoIP returns a place we don't sell to:
       International = USD + English. Prefer US, else first non-Arabic market. */
    function internationalCountry() {
      if (AVAILABLE_COUNTRIES.indexOf('US') !== -1) return 'US';
      for (const c of AVAILABLE_COUNTRIES) {
        if (ARABIC_COUNTRIES.indexOf(c) === -1) return c;
      }
      return AVAILABLE_COUNTRIES[0] || CURRENT_COUNTRY;
    }

    class AsteriLocationSelector extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.form = this.querySelector('#AsteriLocationForm');
        this._handled = false;

        // Language buttons submit the localization form
        this.querySelectorAll('.asteri-location-drawer__lang').forEach((btn) => {
          btn.addEventListener('click', () => this.select(btn.dataset.country, btn.dataset.language));
        });

        // Close button + backdrop click
        this.querySelector('[data-location-selector-close]')?.addEventListener('click', () => this.close());
        this.dialog.addEventListener('click', (e) => {
          if (e.target === this.dialog) this.close();
        });
        this.dialog.addEventListener('cancel', () => {
          try { localStorage.setItem(STORAGE_KEY, '1'); } catch (e) {}
        });

        // Any element with data-location-selector-open reopens the drawer
        document.addEventListener('click', (e) => {
          if (e.target.closest('[data-location-selector-open]')) {
            e.preventDefault();
            this.open();
          }
        });

        // // First visit only, and never inside the theme editor.
        // let seen = null;
        // try { seen = localStorage.getItem(STORAGE_KEY); } catch (e) {}
        // if (!seen && !(window.Shopify && window.Shopify.designMode)) {
        //   this.autoDetect();
        // }
        // First visit only, and never inside the theme editor.
        // Skip in webview (?home_mobile=1): locale page is the chooser; geo
        // auto-detect would submit localization and yank the user mid-pick.
        let seen = null;
        try { seen = localStorage.getItem(STORAGE_KEY); } catch (e) {}
        const isHomeMobile =
          document.documentElement.classList.contains('home-mobile') ||
          /(?:^|[?&])home_mobile=1(?:&|$)/.test(window.location.search) ||
          document.cookie.indexOf('home_mobile=1') !== -1;
        // if (!seen && !isHomeMobile && !(window.Shopify && window.Shopify.designMode)) {          //to enable uncomment this line
        if (false && !seen && !isHomeMobile && !(window.Shopify && window.Shopify.designMode)) { //to disable uncomment this line
          this.autoDetect();
        }
      }

      /* Ask Shopify where the shopper is, then apply the right locale. */
      autoDetect() {
        // Guard so the timeout and the fetch can't both act.
        const once = (fn) => { if (this._handled) return; this._handled = true; fn(); };

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const url = root + 'browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true';

        const controller = ('AbortController' in window) ? new AbortController() : null;
        const timer = setTimeout(() => {
          if (controller) { try { controller.abort(); } catch (e) {} }
          once(() => this.open()); // GeoIP too slow — let them choose
        }, GEO_TIMEOUT_MS);

        fetch(url, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          signal: controller ? controller.signal : undefined
        })
          .then((r) => (r.ok ? r.json() : Promise.reject(new Error('geo http ' + r.status))))
          .then((data) => { clearTimeout(timer); once(() => this.applyDetection(data)); })
          .catch(() => { clearTimeout(timer); once(() => this.open()); }); // failure -> manual drawer
      }

      applyDetection(data) {
        // Raw detected country (may be a place we don't sell to).
        let detected = '';
        try {
          detected =
            (data && data.detected_values && data.detected_values.country && data.detected_values.country.handle) || '';
          if (!detected && data && data.suggestions && data.suggestions[0] &&
              data.suggestions[0].parts && data.suggestions[0].parts.country) {
            detected = data.suggestions[0].parts.country.handle || '';
          }
        } catch (e) {}
        detected = String(detected || '').toUpperCase();

        // Resolve to an enabled market (or International if unknown).
        const targetCountry =
          (detected && AVAILABLE_COUNTRIES.indexOf(detected) !== -1) ? detected : internationalCountry();
        if (!targetCountry) { this.open(); return; }

        // Language from the country rule.
        const wantAr = ARABIC_COUNTRIES.indexOf(targetCountry) !== -1;
        const targetLanguage = pickLanguage(wantAr ? 'ar' : 'en');

        // Already correct? Remember and stay put — no reload, no popup.
        if (targetCountry === CURRENT_COUNTRY &&
            (!targetLanguage || targetLanguage === CURRENT_LANGUAGE)) {
          try { localStorage.setItem(STORAGE_KEY, '1'); } catch (e) {}
          return;
        }

        // Apply once (single silent reload into the right locale).
        this.select(targetCountry, targetLanguage || CURRENT_LANGUAGE);
      }

      open() {
        if (!this.dialog.open) this.dialog.showModal();
      }

      close() {
        try { localStorage.setItem(STORAGE_KEY, '1'); } catch (e) {}
        this.dialog.close();
      }

      select(country, language) {
        try { localStorage.setItem(STORAGE_KEY, '1'); } catch (e) {}
        this.form.querySelector('[name="country_code"]').value = country;
        this.form.querySelector('[name="language_code"]').value = language;
        this.form.submit();
      }
    }

    customElements.define('asteri-location-selector', AsteriLocationSelector);
  })();
